import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useOutletContext } from 'react-router-dom';
import { HubChromeContext } from './hubUi';
import HubHome from './HubHome';
import {
  HubEquipmentDetail,
  HubEquipmentHub,
  HubEquipmentList,
} from './HubEquipment';
import {
  HubEquipmentKycDetail,
  HubEquipmentKycList,
} from './HubEquipmentKyc';
import { HubMemberDetail, HubMemberList, HubMembersHub } from './HubMembers';
import { HubIndentDetail, HubIndentList, HubIndentsHub } from './HubIndents';
import {
  HubActivities,
  HubBuReport,
  HubStaffDetail,
  HubStaffHub,
  HubStaffList,
  HubSystemConfig,
  HubVillageReport,
} from './HubStaff';
import {
  HubMasterBusinessUnits,
  HubMasterEquipmentTypes,
  HubMasterHub,
  HubMasterVillages,
} from './HubMasterData';
import { HubBulkImportHub, HubBulkImportStage } from './HubBulkImport';

/**
 * Full Hub experience — separate shell, real APIs, own lists/filters/details.
 * Never mounts classic sidebar screens; reuses shared modals / master pages when needed.
 */
export default function AdminHubPage() {
  const { hubHomeTick } = useOutletContext() || {};
  const [stack, setStack] = useState([{ id: 'home', label: 'Hub' }]);
  const [headerActions, setHeaderActions] = useState(null);
  const current = stack[stack.length - 1];
  const skipFirstHomeTick = useRef(true);
  const chromeValue = useMemo(() => ({ setHeaderActions }), []);

  useEffect(() => {
    if (hubHomeTick == null) return;
    if (skipFirstHomeTick.current) {
      skipFirstHomeTick.current = false;
      return;
    }
    setStack([{ id: 'home', label: 'Hub' }]);
  }, [hubHomeTick]);

  const push = useCallback((frame) => setStack((s) => [...s, frame]), []);
  const back = useCallback(
    () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s)),
    [],
  );
  const jumpTo = useCallback((index) => setStack((s) => s.slice(0, index + 1)), []);

  return (
    <Box
      px={{ base: 3, md: 5, xl: 6 }}
      py={{ base: 3, md: 4 }}
      w="100%"
      minH="100%"
      color="text.primary"
    >
      <Flex align="center" gap={2} mb={4} flexWrap="wrap" minW={0}>
        <Button
          type="button"
          size="sm"
          variant="outline"
          borderColor="border.subtle"
          color="text.primary"
          flexShrink={0}
          _hover={{ bg: 'bg.input' }}
          onClick={back}
          isDisabled={stack.length <= 1}
        >
          ← Back
        </Button>
        <Flex
          align="center"
          gap={1.5}
          flexWrap="wrap"
          fontSize="13px"
          color="text.secondary"
          minW={0}
          flex="1"
        >
          {stack.map((f, i) => {
            const isLast = i === stack.length - 1;
            return (
              <React.Fragment key={`${f.id}-${i}-${f.label}`}>
                {i > 0 ? <Text opacity={0.4}>›</Text> : null}
                {isLast ? (
                  <Text fontWeight="600" color="text.primary" noOfLines={1}>
                    {f.label}
                  </Text>
                ) : (
                  <Button
                    variant="link"
                    size="sm"
                    color="text.secondary"
                    fontWeight="500"
                    onClick={() => jumpTo(i)}
                    minW={0}
                  >
                    {f.label}
                  </Button>
                )}
              </React.Fragment>
            );
          })}
        </Flex>
        {headerActions ? (
          <Flex gap={2} flexWrap="wrap" flexShrink={0} ml="auto" align="center">
            {headerActions}
          </Flex>
        ) : null}
      </Flex>

      <HubChromeContext.Provider value={chromeValue}>
      {current.id === 'home' && <HubHome push={push} />}
      {current.id === 'equipmentHub' && <HubEquipmentHub push={push} />}
      {current.id === 'equipmentList' && (
        <HubEquipmentList
          push={push}
          filter={current.filter}
          userId={current.userId}
          equipmentType={current.equipmentType}
        />
      )}
      {current.id === 'equipmentDetail' && (
        <HubEquipmentDetail push={push} itemId={current.itemId} />
      )}
      {current.id === 'equipmentKycList' && <HubEquipmentKycList push={push} />}
      {current.id === 'equipmentKycDetail' && (
        <HubEquipmentKycDetail itemId={current.itemId} onDeleted={back} />
      )}
      {current.id === 'membersHub' && <HubMembersHub push={push} />}
      {current.id === 'memberList' && (
        <HubMemberList push={push} filter={current.filter} />
      )}
      {current.id === 'memberDetail' && (
        <HubMemberDetail push={push} itemId={current.itemId} />
      )}
      {current.id === 'indentsHub' && <HubIndentsHub push={push} />}
      {current.id === 'indentList' && (
        <HubIndentList
          push={push}
          filter={current.filter}
          userId={current.userId}
        />
      )}
      {current.id === 'indentDetail' && (
        <HubIndentDetail push={push} itemId={current.itemId} />
      )}
      {current.id === 'staffHub' && <HubStaffHub push={push} />}
      {current.id === 'staffList' && <HubStaffList push={push} />}
      {current.id === 'staffDetail' && (
        <HubStaffDetail push={push} itemId={current.itemId} />
      )}
      {current.id === 'activities' && (
        <HubActivities staffId={current.staffId} />
      )}
      {current.id === 'systemConfig' && <HubSystemConfig />}
      {current.id === 'villageReport' && <HubVillageReport push={push} />}
      {current.id === 'buReport' && <HubBuReport push={push} />}
      {current.id === 'masterHub' && <HubMasterHub push={push} />}
      {current.id === 'masterVillages' && <HubMasterVillages />}
      {current.id === 'masterBusinessUnits' && (
        <HubMasterBusinessUnits push={push} />
      )}
      {current.id === 'masterEquipmentTypes' && <HubMasterEquipmentTypes />}
      {current.id === 'bulkImportHub' && <HubBulkImportHub push={push} />}
      {current.id === 'bulkImportStage' && (
        <HubBulkImportStage stageId={current.stageId} />
      )}
      </HubChromeContext.Provider>
    </Box>
  );
}
